import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type Board, type UploadUrlResponse } from '@air/api/types';
import { callWithRetry, getExponentialBackoff } from '@air/call-with-retry';
import { NetworkStatusInfo } from '@air/classes-network-status-info';
import { isAirror, MAX_ANCESTOR_COUNT_EXCEEDED } from '@air/errors';
import {
  setSmallUploadCancellablesAction,
  setSmallUploadProgressAction,
  setUploadCompletedAction,
  setUploadFailedAction,
  setUploadInstabilityDetectedAction,
  setUploadPreparingAction,
  setUploadUploadingAction,
  setUploadUrlInfoAction,
  smallUploadByIdSelector,
  type Upload,
  UploadStatus,
  uploadLogger,
  useUploaderStore,
} from '@air/redux-uploader';
import { uploadFile } from '@air/upload';

import { UPLOAD_NETWORK_STATUS_CHECK_TIMEOUT_MS } from '../constants';
import { getUploadUrlForRetry, getUploadUrlType } from '../getUploadUrlForRetry';
import { isRetryableUploadTransferError } from '../isRetryableUploadTransferError';
import { type UploadFileBaseParams } from '../types';
import { isAbortError, isUploadTransferInstabilityError } from '../uploadErrors';

export type UploadSmallParams = {
  uploadId: Upload['id'];
  parentBoardId?: Board['id'];
  onUploadStarted: UploadFileBaseParams['onUploadStarted'];
  onUploadFinished: (params: {
    uploadId: Upload['id'];
    uploadUrlType?: Upload['uploadUrlType'];
  }) => Promise<void> | void;
  getUploadUrls: (upload: Upload) => Promise<UploadUrlResponse[0] | undefined>;
  onError: (params: {
    upload: Upload;
    error: unknown;
    failedStage?: 'small_upload';
    uploadUrlType?: Upload['uploadUrlType'];
  }) => void;
};

const MAX_SMALL_UPLOAD_RETRIES = 5;

export const useUploadSmall = () => {
  const store = useUploaderStore();
  const dispatch = useDispatch();

  const uploadSmall = useCallback(
    async ({ uploadId, getUploadUrls, onUploadStarted, onUploadFinished, onError }: UploadSmallParams) => {
      const upload = smallUploadByIdSelector(store.getState(), uploadId);

      if (upload && upload.status === UploadStatus.queued) {
        dispatch(setUploadPreparingAction({ uploadId: upload.id }));
        let uploadUrlInfo = upload.s3Info.uploadUrlInfo;
        let currentUploadUrl = uploadUrlInfo?.url;

        try {
          if (!uploadUrlInfo) {
            uploadUrlInfo = await getUploadUrls(upload);
            if (uploadUrlInfo) {
              dispatch(setUploadUrlInfoAction({ uploadId: upload.id, uploadUrlInfo }));
            }
          }

          dispatch(setUploadUploadingAction({ uploadId: upload.id }));
          if (uploadUrlInfo) {
            const activeUploadUrlInfo = uploadUrlInfo;
            currentUploadUrl = activeUploadUrlInfo.url;
            const onProgress: UploadFileBaseParams['onProgress'] = ({ progress, abort }) => {
              if (progress) {
                dispatch(setSmallUploadProgressAction({ abort, progress, uploadId: upload.id }));
              }
            };
            let retryCount = 0;

            const onUploadStart = (xhr: XMLHttpRequest) => {
              dispatch(
                setSmallUploadCancellablesAction({ uploadId: upload.id, uploadUrlInfo: activeUploadUrlInfo, xhr }),
              );
              onUploadStarted({
                maxRetryCount: MAX_SMALL_UPLOAD_RETRIES,
                retryCount,
                uploadId: upload.id,
                uploadUrlType: getUploadUrlType({
                  currentUrl: currentUploadUrl ?? activeUploadUrlInfo.url,
                  uploadUrlInfo: activeUploadUrlInfo,
                }),
              });
            };

            const { mime } = activeUploadUrlInfo;

            await callWithRetry({
              action: () =>
                uploadFile({
                  contentType: mime,
                  file: upload.file,
                  onProgress,
                  onStart: onUploadStart,
                  url: currentUploadUrl ?? activeUploadUrlInfo.url,
                }),
              getRetryDelay: (retries) =>
                getExponentialBackoff({
                  attempt: retries,
                  factor: 2,
                  initialDelay: 200,
                  maxDelay: 30_000,
                }),
              onError: (error) => {
                if (isUploadTransferInstabilityError(error)) {
                  dispatch(setUploadInstabilityDetectedAction());
                }
              },
              shouldRetry: async (retries, error) => {
                const updatedUpload = smallUploadByIdSelector(store.getState(), upload.id);

                if (!updatedUpload || updatedUpload.status === UploadStatus.paused) {
                  return false;
                }

                if (!isRetryableUploadTransferError(error)) {
                  return false;
                }

                if (retries >= MAX_SMALL_UPLOAD_RETRIES) {
                  return false;
                }

                const isOnline = await NetworkStatusInfo.checkIfOnline({
                  timeout: UPLOAD_NETWORK_STATUS_CHECK_TIMEOUT_MS,
                });

                if (!isOnline) {
                  return false;
                }

                retryCount = retries + 1;
                const nextUploadUrl = getUploadUrlForRetry({
                  currentUrl: currentUploadUrl ?? activeUploadUrlInfo.url,
                  error,
                  uploadUrlInfo: activeUploadUrlInfo,
                });

                if (nextUploadUrl !== currentUploadUrl) {
                  uploadLogger.fallbackUrlSelected({ uploadId: upload.id });
                }

                currentUploadUrl = nextUploadUrl;

                return true;
              },
            });

            await onUploadFinished({
              uploadId: upload.id,
              uploadUrlType: getUploadUrlType({
                currentUrl: currentUploadUrl ?? activeUploadUrlInfo.url,
                uploadUrlInfo: activeUploadUrlInfo,
              }),
            });
            dispatch(setUploadCompletedAction({ uploadId: upload.id }));
          } else {
            // TODO: handle error
          }
        } catch (error) {
          if (!isAbortError(error)) {
            dispatch(
              setUploadFailedAction({
                errorMessage:
                  isAirror(error) && error.type === MAX_ANCESTOR_COUNT_EXCEEDED.type
                    ? 'Max ancestor count exceeded'
                    : 'Failed',
                uploadId: upload.id,
              }),
            );
          }
          const uploadUrlType = uploadUrlInfo
            ? getUploadUrlType({ currentUrl: currentUploadUrl ?? uploadUrlInfo.url, uploadUrlInfo })
            : upload.uploadUrlType;

          onError({ error, failedStage: 'small_upload', upload, uploadUrlType });
        }
      }
    },
    [dispatch, store],
  );

  return {
    uploadSmall,
  };
};
